import { useEffect, useState, type FormEvent } from "react";
import { LoaderCircle, Leaf } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useSession, type User } from "@/lib/runs";

export function AuthDialog() {
  const session = useSession();
  const [mode, setMode] = useState<"email" | "legacy">("email");
  const [email, setEmail] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [code, setCode] = useState("");
  const [retryAt, setRetryAt] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [expiryMinutes, setExpiryMinutes] = useState(5);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    const update = () =>
      setRemaining(Math.max(0, Math.ceil((retryAt - Date.now()) / 1000)));
    update();
    if (!retryAt) return;
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [retryAt]);

  async function sendCode() {
    setBusy(true);
    setError("");
    try {
      const result = await api<{
        email: string;
        resend_after_seconds: number;
        expires_in_seconds: number;
      }>("/auth/email/request-code", {
        method: "POST",
        body: { email: email.trim() },
      });
      setEmail(result.email);
      setSentTo(result.email);
      setCode("");
      setRetryAt(Date.now() + result.resend_after_seconds * 1000);
      setExpiryMinutes(Math.ceil(result.expires_in_seconds / 60));
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "验证码发送失败，请重试。",
      );
    } finally {
      setBusy(false);
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    if (mode === "email" && !sentTo) {
      await sendCode();
      return;
    }
    const data = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      const result = await api<{ user: User }>(
        mode === "email" ? "/auth/email/verify" : "/auth/login",
        {
          method: "POST",
          body:
            mode === "email"
              ? { email: sentTo, code }
              : {
                  username: data.get("username"),
                  password: data.get("password"),
                },
        },
      );
      session.setUser(result.user);
      setCode("");
      setSentTo("");
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "暂时无法登录，请重试。",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={session.loginOpen}
      onOpenChange={(open) => {
        if (!open) session.closeLogin();
        setError("");
      }}
    >
      <DialogContent className="auth-dialog">
        <span className="brand-icon mb-2">
          <Leaf size={20} />
        </span>
        <DialogTitle>
          {mode === "email" ? "邮箱验证码登录" : "原有账号登录"}
        </DialogTitle>
        <DialogDescription>
          {mode === "email"
            ? "首次验证后自动创建账户。输入草稿会保留。"
            : "使用原来的账号和密码，继续已有对话。"}
        </DialogDescription>
        <form className="grid gap-5 mt-3" onSubmit={submit} key={mode}>
          {mode === "email" ? (
            <>
              <label className="field">
                <span>邮箱</span>
                <Input
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  maxLength={254}
                  placeholder="你的邮箱地址"
                  value={email}
                  disabled={busy}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    setSentTo("");
                    setCode("");
                    setError("");
                  }}
                />
              </label>
              {sentTo && (
                <>
                  <p className="text-sm text-muted-foreground" role="status">
                    验证码已发送，{expiryMinutes}{" "}
                    分钟内有效。未收到时可检查垃圾邮件。
                  </p>
                  <label className="field">
                    <span>验证码</span>
                    <Input
                      name="code"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      autoFocus
                      required
                      pattern="[0-9]{6}"
                      minLength={6}
                      maxLength={6}
                      placeholder="6 位数字验证码"
                      value={code}
                      disabled={busy}
                      onChange={(event) =>
                        setCode(event.target.value.replace(/\D/g, ""))
                      }
                    />
                  </label>
                </>
              )}
            </>
          ) : (
            <>
              <label className="field">
                <span>原有账号</span>
                <Input
                  name="username"
                  required
                  minLength={3}
                  maxLength={80}
                  autoComplete="username"
                  placeholder="原来的账号名称"
                  disabled={busy}
                />
              </label>
              <label className="field">
                <span>密码</span>
                <Input
                  name="password"
                  type="password"
                  required
                  minLength={8}
                  maxLength={256}
                  autoComplete="current-password"
                  disabled={busy}
                />
              </label>
            </>
          )}
          {error && (
            <p className="text-danger-text text-sm" role="alert">
              {error}
            </p>
          )}
          <Button type="submit" disabled={busy}>
            {busy && <LoaderCircle className="animate-spin" size={16} />}
            {mode === "email" && !sentTo ? "获取验证码" : "登录并继续"}
          </Button>
          {mode === "email" && sentTo && (
            <Button
              type="button"
              variant="ghost"
              disabled={busy || remaining > 0}
              onClick={() => void sendCode()}
            >
              {remaining > 0 ? `${remaining} 秒后可重新发送` : "重新发送验证码"}
            </Button>
          )}
        </form>
        <Button
          variant="ghost"
          disabled={busy}
          onClick={() => {
            setMode(mode === "email" ? "legacy" : "email");
            setError("");
          }}
        >
          {mode === "email" ? "使用原有账号和密码" : "返回邮箱验证码登录"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
